import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { map } from 'lit/directives/map.js';
import BoilerplateForm from './boilerplate_form';

class BackInStockForm extends BoilerplateForm {
  static get properties() {
    return {
      product: { type: Object, attribute: true },
      variant_id: { type: Number, attribute: 'variant-id' },
      customer: { type: Object },
      notification_channel: { type: String, state: true },
    };
  }

  constructor() {
    super();
    this.useUrlParams = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.notification_channel = 'email'; // Defaults to email channel for notification sign up

    document.addEventListener('back-in-stock-form:reset', this.resetForm.bind(this));
  }

  resetForm() {
    this.notification_channel = 'email';
    this.objectData = {};
    this.updateFormState('initial');
  }

  handleChannelSelect(event) {
    this.notification_channel = event.target.value;
  }

  async handleSubmit(event) {
    event.preventDefault();

    this.success = false;
    this.errors = {};
    this.genericErrorSegments = [];

    if (!this.objectData.email && !this.objectData.phone) {
      this.handleErrors(window.theme.strings.back_in_stock.missing_contact_info);
      return;
    }

    if (!this.objectData.variant) { 
      this.handleErrors(window.theme.strings.back_in_stock.missing_variant);
      return;
    }

    if (!window.BIS || !window.BIS.create) {
      return;
    }

    const email = this.objectData?.email || null;
    const variantId = parseInt(this.objectData?.variant);
    const phone_number = this.objectData?.phone || null;
    try {
      const result = await window.BIS.create(email, variantId, this.product.id, {
        ...(phone_number ? { phone_number } : {})
      });

      if (result.errors && Object.keys(result.errors).length > 0) {
        let errorMessage = '';
        for (const error of Object.values(result.errors)) {
          errorMessage += error + '\n';
        }
        this.handleErrors(errorMessage);
        return;
      }
      this.handleSuccess();
    } catch (error) {
      this.handleErrors(window.theme.strings.back_in_stock.notification_create_error);
    }
  }

  handleSuccess() {
    this.errors = {};
    this.genericErrorSegments = [];
    this.success = true;
    this.loading = false;

    this.updateFormState('success');
    const event = new CustomEvent('back-in-stock-form:submit');
    document.dispatchEvent(event);
  }

  renderVariantSelect() {
    if (this.variant_id && !this.product?.variants?.length) {
      return html`
        <input type="hidden" name="variant" value=${ this.variant_id }>
      `;
    }

    const soldOutVariants = this.product?.variants?.filter((variant) => {
      if (variant.attributes && !variant.attributes.Sellable) {
        return true;
      }
      if (!variant.available) {
        return true;
      }
      return false;
    }) || this.product?.variants || [];
    console.log({ soldOutVariants });

    return html`
      <float-select
        name="variant"
        id="BIS_variant"
        error=${ window.theme.strings.back_in_stock.missing_variant }
        .options=${ soldOutVariants.map((variant) => ({
          label: variant.title || variant?.attributes?.Size,
          value: variant.id,
          selected: variant.id == this.variant_id,
        }))}
      >
      </float-select>
    `
  }

  renderEmailInput() {
    return html`
      <float-field
        type="email"
        name="email"
        id="back_in_stock_email"
        label="Email address"
        autocomplete="email"
        placeholder="Email address"
        error=${ window.theme.strings.back_in_stock.missing_contact_info }
        required
        value=${ this.customer?.email || '' }
      >
      </float-field>
    `
  }

  renderPhoneInput() {
    return html`
      <float-phone-field
        name="phone"
        id="back_in_stock_phone"
        label="Phone"
        autocomplete="tel"
        placeholder="Phone"
        error=${ window.theme.strings.back_in_stock.missing_contact_info }
        required
        value=${ this.customer?.phone || '' }
        .countryCode=${ this.customer?.country_code || '' }
      >
      </float-phone-field>
    `
  }

  renderCustomerContact() {
    return html`
      <div class="bis_channel_select button-group">
        <button
          type="button"
          class="button ${ this.notification_channel === 'email' ? '_primary' : '_secondary' }"
          @click=${ this.handleChannelSelect }
          value="email"
        >Email</button>
        <button
          type="button"
          class="button ${ this.notification_channel === 'sms' ? '_primary' : '_secondary' }"
          @click=${ this.handleChannelSelect }
          value="sms"
        >SMS</button>
      </div>
      ${ this.notification_channel === 'email'
        ? this.renderEmailInput()
        : this.renderPhoneInput()
      }
    `
  }

  renderFormElements() {
    return html`
      ${ this.renderVariantSelect() }
      ${ this.renderCustomerContact() }
    `;
  }

  renderSuccess() {
    return html`
      ${ this.success ? html`
          <p class="bis_success_text">
            ${ window.theme.strings.back_in_stock.success_text }
          </p>
        ` : nothing
      }
    `;
  }

  renderButton() {
    return html`
      <button
        type="submit"
        class="button _primary"
        ?disabled=${ this.loading }
      >
        ${ this.loading
          ? html`<bouncing-ellipsis></bouncing-ellipsis>`
          : window.theme.strings.back_in_stock.button
        }
      </button>
    `;
  }
}

window.customElements.get('back-in-stock-form') || window.customElements.define('back-in-stock-form', BackInStockForm);